/**
 * Utilidad centralizada para normalizar y validar números telefónicos
 * Sincronizada entre FRONT y BACK
 * Soporta múltiples países como se define en countryData.js
 */

import { COUNTRIES } from './countryData';

/**
 * Extrae solo los dígitos de un teléfono
 */
export const extractDigits = (phone) => {
  return phone.replace(/\D/g, '');
};

/**
 * Detecta el país basado en el código de teléfono
 * Ej: "525511223344" → "MX" (comienza con 52)
 */
export const detectCountryFromPhone = (phone) => {
  const digits = extractDigits(phone);

  for (const country of COUNTRIES) {
    const countryCodeDigits = country.phoneCode.replace(/\D/g, '');
    if (digits.startsWith(countryCodeDigits)) {
      return country.code;
    }
  }

  return null;
};

/**
 * Valida si un teléfono tiene el formato correcto para su país
 * Soporta: "+525511223344", "525511223344", "5511223344"
 */
export const validatePhoneForCountry = (phone, countryCode) => {
  const country = COUNTRIES.find(c => c.code === countryCode);
  if (!country) {
    return { valid: false, message: `País ${countryCode} no soportado` };
  }

  const digits = extractDigits(phone);
  const countryCodeDigits = country.phoneCode.replace(/\D/g, '');

  // Caso 1: Ya tiene el código de país (ej: "525511223344")
  if (digits.startsWith(countryCodeDigits)) {
    const phoneOnlyDigits = digits.slice(countryCodeDigits.length);
    if (phoneOnlyDigits.length === country.phoneDigits) {
      return { valid: true };
    }
    return {
      valid: false,
      message: `${country.name} requiere ${country.phoneDigits} dígitos después del código ${country.phoneCode}`,
    };
  }

  // Caso 2: Solo los dígitos sin código (ej: "5511223344")
  if (digits.length === country.phoneDigits) {
    return { valid: true };
  }

  return {
    valid: false,
    message: `${country.name} requiere ${country.phoneDigits} dígitos (recibido: ${digits.length})`,
  };
};

/**
 * Normaliza el teléfono al formato internacional
 * Entrada: "5511223344" + "MX" → Salida: "525511223344"
 * Entrada: "525511223344" + "MX" → Salida: "525511223344" (sin cambios)
 */
export const normalizePhoneToInternational = (phone, countryCode) => {
  const country = COUNTRIES.find(c => c.code === countryCode);
  if (!country) {
    throw new Error(`País ${countryCode} no soportado`);
  }

  const digits = extractDigits(phone);
  const countryCodeDigits = country.phoneCode.replace(/\D/g, '');

  // Si ya tiene el código, retornar igual
  if (digits.startsWith(countryCodeDigits)) {
    return digits;
  }

  // Si no lo tiene, agregarlo
  return countryCodeDigits + digits;
};

/**
 * Genera el JID de WhatsApp
 * Entrada: "5511223344" (MX) → Salida: "521511223344@s.whatsapp.net" (con el 1 requerido)
 * Entrada: "525511223344" (MX) → Salida: "521511223344@s.whatsapp.net" (sin duplicar)
 * 
 * NOTA: Ciertos países como México requieren un dígito adicional:
 * - México (MX): +52 → 521 en WhatsApp
 * - Argentina (AR): +54 → 549 en WhatsApp
 */
export const getWhatsappJid = (phone, countryCode) => {
  const digits = extractDigits(phone);

  // Si ya tiene @ es un JID, retornar igual
  if (phone.includes('@')) {
    return phone;
  }

  // Detectar país si no se proporciona
  const detectedCountry = countryCode || detectCountryFromPhone(digits);
  const country = COUNTRIES.find(c => c.code === detectedCountry) || COUNTRIES.find(c => c.code === 'MX');

  // Asegurar que tenga código de país
  let finalDigits = digits;
  const countryCodeDigits = country.phoneCode.replace(/\D/g, '');
  
  if (!digits.startsWith(countryCodeDigits)) {
    finalDigits = countryCodeDigits + digits;
  }

  // Algunos países requieren un dígito adicional para WhatsApp
  // México: 521XXXXXXXXXX (no 525XXXXXXXXXX)
  // Argentina: 549XXXXXXXXXX (no 545XXXXXXXXXX)
  if (country.code === 'MX') {
    // Reemplazar solo "52" con "521"
    if (finalDigits.startsWith('521') && finalDigits.length === 13) {
      // cuenta vieja - ok
    } else if (finalDigits.startsWith('52') && finalDigits.length === 12) {
      // cuenta nueva - ok
    } else if (finalDigits.length === 10) {
      // sin código de país - asumir formato nuevo
      finalDigits = '52' + finalDigits;
    }
  } else if (country.code === 'AR' && !finalDigits.startsWith('549')) {
    // Reemplazar solo "54" con "549"
    if (finalDigits.startsWith('54')) {
      finalDigits = '549' + finalDigits.slice(2);
    }
  }

  return `${finalDigits}@s.whatsapp.net`;
};

/**
 * Limpia el teléfono para almacenamiento normalizado
 * (solo dígitos con código de país internacional)
 */
export const normalizePhoneForStorage = (phone, countryCode) => {
  const validation = validatePhoneForCountry(phone, countryCode);
  if (!validation.valid) {
    throw new Error(`Teléfono inválido: ${validation.message}`);
  }

  return normalizePhoneToInternational(phone, countryCode);
};

/**
 * Obtiene información del país
 */
export const getCountryInfo = (countryCode) => {
  return COUNTRIES.find(c => c.code === countryCode) || null;
};

/**
 * Limpia teléfono para mostrar al usuario (sin código de país)
 */
export const getPhoneWithoutCountryCode = (phone, countryCode) => {
  const country = COUNTRIES.find(c => c.code === countryCode);
  if (!country) return phone;

  const digits = extractDigits(phone);
  const countryCodeDigits = country.phoneCode.replace(/\D/g, '');
  
  if (digits.startsWith(countryCodeDigits)) {
    return digits.slice(countryCodeDigits.length);
  }

  return digits;
};
